import { useEffect, useMemo, useRef } from "react";
import { transformDraftIncidentData } from "../utils/formGenUtils/draftFormattingUtils";

interface IUseFetchedIncidentParams {
  formId: string;
  createOrUpdateSuccess: boolean;
  saveOrUpdateSuccess: boolean;
  getIncident: any;
  getDraft: any;
  isIncidentDraft: boolean;
  setStatusSuccess: boolean;
}

const useFetchedIncident = ({
  formId,
  createOrUpdateSuccess,
  saveOrUpdateSuccess,
  getIncident,
  getDraft,
  isIncidentDraft,
  setStatusSuccess,
}: IUseFetchedIncidentParams) => {
  const {
    data: incidentData,
    isSuccess: incidentDataSuccess,
    isLoading: incidentDataLoading,
    isError: incidentDataError,
    refetch: incidentDataRefetch,
  } = getIncident;
  const {
    data: draftIncidentData,
    isLoading: draftDataLoading,
    isSuccess: draftDataSuccess,
    isError: draftDataError,
    refetch: draftDataRefetch,
  } = getDraft;

  const selectedIncidentDetails =
    incidentDataLoading || !incidentDataSuccess
      ? null
      : incidentData?.data?.data;

  const draftIncidentDetails =
    draftDataLoading || !draftDataSuccess
      ? null
      : draftIncidentData?.data?.data;

  // Memoize the transformed draft data to prevent creating a new object reference
  // on every render. Without this, every IncidentLayout re-render (tab navigation,
  // window focus, etc.) creates a new selectedIncident reference, which triggers
  // shouldUpdateForm=true in IncidentForm and wipes user edits via enableReinitialize.
  const draftJsonRef = useRef<string | null>(null);
  const cachedDraftRef = useRef<any>(null);

  const selectedIncident = useMemo(() => {
    if (!isIncidentDraft) return selectedIncidentDetails;

    // Cache the transformed draft data by comparing the raw JSON string
    // to avoid unnecessary re-transforms that produce new object references
    const currentJson = draftIncidentDetails?.Incident_Json__c ?? null;
    const currentId = draftIncidentDetails?.Id ?? null;
    const cacheKey = currentId ? `${currentId}:${currentJson}` : null;

    if (cacheKey && cacheKey === draftJsonRef.current && cachedDraftRef.current) {
      return cachedDraftRef.current;
    }

    const transformed = transformDraftIncidentData(draftIncidentDetails);
    if (cacheKey) {
      draftJsonRef.current = cacheKey;
      cachedDraftRef.current = transformed;
    }
    return transformed;
  }, [isIncidentDraft, draftIncidentDetails, selectedIncidentDetails]);

  useEffect(() => {
    if (formId) {
      createOrUpdateSuccess && incidentDataRefetch();
      setStatusSuccess && incidentDataRefetch();
      saveOrUpdateSuccess && draftDataRefetch();
    }
  }, [createOrUpdateSuccess, saveOrUpdateSuccess, formId, setStatusSuccess]);

  const isDataLoading = isIncidentDraft
    ? draftDataLoading
    : incidentDataLoading;
  const isDataError = isIncidentDraft ? draftDataError : incidentDataError;

  return {
    selectedIncident,
    isIncidentLoading: isDataLoading && !isDataError,
    isDataError,
  };
};

export default useFetchedIncident;
